import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { NoticeOutputPopupFormPayload } from "./NoticeOutputPopupForm";
import { useGetChatSimulators } from "./useGetChatSimulators";
import { useCallback, useState } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import {
  UpdateNoticeOutputsRequest,
  useNoticeApi,
} from "@/v2/entities/notice/Notice.api";
import { Notice } from "@/v2/entities/notice/Notice.model";
import { toast } from "sonner";
import { useQueryClient } from "react-query";

function getSelectedSimulatorIds(notice?: Notice): number[] {
  if (notice == null || notice.noticeOutputs == null) {
    return [];
  }

  return notice.noticeOutputs
    .filter(({ type }) => type === "Popup")
    .map(({ simulatorId }) => Number(simulatorId));
}

interface PublishNoticeOutputProps {
  notice: Notice;
  payloadToPublish: NoticeOutputPopupFormPayload;
  onPublish: () => Promise<void>;
}

export function PublishNoticeOutput({
  notice,
  payloadToPublish,
  onPublish,
}: PublishNoticeOutputProps) {
  const { updateNoticeOutputs } = useNoticeApi();
  const { chatSimulators } = useGetChatSimulators();
  const queryClient = useQueryClient();

  const [selectedSimulatorIds, setSelectedSimulatorIds] = useState<number[]>(
    () => getSelectedSimulatorIds(notice)
  );

  const handleSelectSimulator = useCallback((simulatorId: number) => {
    setSelectedSimulatorIds((prev) => {
      if (prev.includes(simulatorId)) {
        return prev.filter((id) => id !== simulatorId);
      }

      return [...prev, simulatorId];
    });
  }, []);

  const handlePublish = useCallback(async () => {
    if (notice == null) {
      return;
    }

    const noticeId = notice.id;
    const request: UpdateNoticeOutputsRequest = {
      noticeId: notice.id,
      outputs: selectedSimulatorIds.map((simulatorId) => ({
        noticeId: noticeId,
        type: "Popup",
        name: payloadToPublish.title,
        simulatorId: simulatorId.toString(),
        title: payloadToPublish.title,
        message: payloadToPublish.message,
        ctaLabel: payloadToPublish.ctaLabel,
        enabled: true,
      })),
    };

    const updateNoticeOutputsPromise = updateNoticeOutputs(request);
    toast.promise(updateNoticeOutputsPromise, {
      loading: "Publishing notice outputs...",
      success: async () => {
        // Invalidate all notices queries to refresh the list and detail views
        await queryClient.invalidateQueries(["notices"]);
        await onPublish();
        return "Notice outputs published successfully";
      },
      error: () => "Failed to publish notice outputs",
    });
  }, [
    notice,
    payloadToPublish,
    selectedSimulatorIds,
    onPublish,
    updateNoticeOutputs,
    queryClient,
  ]);

  return (
    <Flex direction="column" gap="5">
      <Flex direction="column" gap="2">
        <Text size="5" weight="medium" highContrast>
          Select Simulators
        </Text>

        <Text size="2" color="gray">
          Select the simulators that will receive the notice popup.
        </Text>
      </Flex>

      <Flex direction="column" gap="2">
        {chatSimulators?.map((simulator) => (
          <Flex key={simulator.id} align="center" gap="2">
            <Switch
              id={simulator.id.toString()}
              checked={selectedSimulatorIds.includes(simulator.id)}
              onCheckedChange={() => handleSelectSimulator(simulator.id)}
            />
            <Label htmlFor={simulator.id.toString()}>{simulator.name}</Label>
          </Flex>
        ))}
      </Flex>

      <Button
        variant="solid"
        size="2"
        color="gray"
        highContrast
        onClick={handlePublish}
      >
        Publish
      </Button>
    </Flex>
  );
}
